/**
 * Partner Memory Tab
 *
 * Displays a partner's memory card inline on the Partners page.
 * Fetches the partner's memory and renders the shared MemoryCard
 * or EmptyMemoryCard component. Used as a tab content panel.
 */

import { Loader2 } from "lucide-react";
import { Card } from "../ui/card";
import { MemoryPanel } from "./MemoryPanel";
import { EmptyMemoryCard } from "../memory/EmptyMemoryCard";
import {
  useSpeakerMemory,
  useRegenerateSpeakerMemory,
} from "../../hooks/useSpeakerMemories";

interface PartnerMemoryTabProps {
  partnerId: string;
  partnerName: string;
}

export function PartnerMemoryTab({
  partnerId,
  partnerName,
}: PartnerMemoryTabProps) {
  const { data: memory, isLoading, refetch } = useSpeakerMemory(partnerId);
  const regenerate = useRegenerateSpeakerMemory();

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span>Loading {partnerName}&apos;s memory...</span>
        </div>
      </Card>
    );
  }

  if (!memory) {
    return (
      <EmptyMemoryCard
        title={`${partnerName}'s Memory`}
        description={`No memory yet — the therapist will build a profile about ${partnerName} after analyzing your first reflection together.`}
        onRegenerate={async () => {
          await regenerate.mutateAsync(partnerId);
          void refetch();
        }}
        regeneratePending={regenerate.isPending}
      />
    );
  }

  return <MemoryPanel memory={memory} onRefresh={() => refetch()} />;
}
